'use client';

import Image from 'next/image';
import Link from 'next/link';
import { ReactNode } from 'react';
import { logos } from './assets/images';
import { COMPANY } from '@voley/shared-types/constants';
import { BRAND_BLUE } from '@voley/shared-types/constants';

export default function MarketingLayout({ children }: { children: ReactNode }) {
  return (
    <>
      {/* Add global CSS overrides for external pages */}
      <style jsx global>{`
        /* External pages: ensure heading text color using CSS variable */
        .text-text-heading {
          color: var(--color-text-primary, #111827) !important;
        }
        /* System UI stack (Apple SF Pro first) for a smooth, tech feel */
        .font-system {
          font-family: -apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text",
            "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", "Apple Color Emoji",
            "Segoe UI Emoji", "Segoe UI Symbol", sans-serif !important;
        }
      `}</style>
      
      <div className="min-h-screen flex flex-col" style={{ backgroundColor: '#ffffff', color: '#1f2937', '--app-font-scale': '1' } as React.CSSProperties}>
        {/* Header/Navbar */}
        <header className="fixed top-0 left-0 right-0 z-50 bg-white">
        <div className="px-6 sm:px-8 lg:px-12 xl:px-16 2xl:px-20">
          <div className="flex items-center py-4">
            {/* Logo */}
            <div className="flex justify-start">
              <Link href="/home" className="inline-flex items-center">
                {/* Mobile logo: BallOnly */}
                <Image
                  src={logos.ballOnly}
                  alt="Voley"
                  width={40}
                  height={40}
                  className="h-9 w-9 md:hidden"
                  priority
                />
                {/* Desktop logo: wordmark */}
                <Image
                  src={logos.voleyT}
                  alt="Voley"
                  width={120}
                  height={28}
                  className="hidden md:inline h-7 w-auto lg:h-8 -ml-1"
                  priority
                />
              </Link>
            </div>
            
            {/* Desktop Navigation */}
            <nav className="hidden md:flex space-x-6 ml-8">
              <Link 
                href="/solutions" 
                className="text-lg font-normal text-gray-900 hover:text-gray-600"
              >
                Solutions
              </Link>
              <Link 
                href="/pricing" 
                className="text-lg font-normal text-gray-900 hover:text-gray-600"
              >
                Pricing
              </Link>
              <div className="relative group">
                <button className="text-lg font-normal text-gray-900 hover:text-gray-600 flex items-center">
                  Resources
                  <svg className="ml-1 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <div className="absolute left-0 top-full pt-3 w-48 z-50 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                  <div className="bg-white rounded-md shadow-lg py-1 border border-gray-200">
                    <Link href="/docs" className="block px-4 py-2 text-base text-gray-500 hover:text-gray-900 hover:bg-gray-50">
                      Docs & Guides
                    </Link>
                    <Link href="/blog" className="block px-4 py-2 text-base text-gray-500 hover:text-gray-900 hover:bg-gray-50">
                      Blog
                    </Link>
                  </div>
                </div>
              </div>
              <div className="relative group">
                <button className="text-lg font-normal text-gray-900 hover:text-gray-600 flex items-center">
                  Company
                  <svg className="ml-1 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <div className="absolute left-0 top-full pt-3 w-48 z-50 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                  <div className="bg-white rounded-md shadow-lg py-1 border border-gray-200">
                    <Link href="/about" className="block px-4 py-2 text-base text-gray-500 hover:text-gray-900 hover:bg-gray-50">
                      About Us
                    </Link>
                    <Link href="/contact" className="block px-4 py-2 text-base text-gray-500 hover:text-gray-900 hover:bg-gray-50">
                      Contact
                    </Link>
                  </div>
                </div>
              </div>
            </nav>
            
            {/* Call to Action Buttons (desktop only) */}
            <div className="hidden md:flex items-center ml-auto space-x-4">
              <Link
                href="/login"
                className="text-lg font-medium text-gray-900 hover:text-gray-600"
              >
                Login
              </Link>
              <Link
                href="/talk-to-sales"
                className="whitespace-nowrap inline-flex items-center justify-center px-3 py-1 border border-gray-200 rounded-md text-lg font-medium text-gray-700 bg-white shadow-sm transition-all duration-200 hover:bg-brand hover:text-white hover:border-brand hover:shadow-md"
              >
                Talk to Sales
              </Link>
              <Link
                href="/get-started"
                className="whitespace-nowrap inline-flex items-center justify-center px-3 py-1 border border-brand rounded-md shadow-sm text-lg font-medium text-white bg-brand transition-all duration-200 hover:bg-[#2a6bb0] hover:border-[#2a6bb0] hover:shadow-md"
              >
                Get Started
              </Link>
            </div>
            
            {/* Mobile minimal CTAs */}
            <div className="md:hidden flex items-center gap-2 sm:gap-3 ml-auto">
              <Link href="/solutions" className="text-xs sm:text-sm text-gray-700 hover:text-gray-900 whitespace-nowrap font-medium">Solutions</Link>
              <Link href="/pricing" className="text-xs sm:text-sm text-gray-700 hover:text-gray-900 whitespace-nowrap font-medium">Pricing</Link>
              <Link href="/contact-mobile" className="text-xs sm:text-sm font-semibold px-3 py-1.5 rounded-full text-white hover:opacity-90 whitespace-nowrap transition-opacity" style={{ backgroundColor: BRAND_BLUE }}>Contact</Link>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="grow relative pt-24 sm:pt-28">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200">
        <div className="max-w-7xl mx-auto py-10 sm:py-12 px-4 sm:px-6 lg:py-16 lg:px-8">
          <div className="lg:grid lg:grid-cols-3 lg:gap-8">
            <div className="lg:col-span-1">
              <Image
                src={logos.voleyT}
                alt="Voley"
                width={110}
                height={30}
                className="h-7 w-auto sm:h-8"
              />
              <p className="text-gray-500 text-sm sm:text-base mt-4">
                Insurance agency communication with automatic HawkSoft logging, E&O documentation, and compliance guardrails.
              </p>
              <p className="text-gray-400 text-sm mt-2">
                support@voley.us
              </p>
              <div className="flex items-center gap-4 mt-4 sm:mt-6">
                <a href="https://www.linkedin.com/company/letsvoley/posts/?feedView=all" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-brand transition-colors">
                  <span className="sr-only">LinkedIn</span>
                  <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
                  </svg>
                </a>
              </div>
            </div>
            <div className="mt-8 sm:mt-10 lg:mt-0 grid grid-cols-2 sm:grid-cols-4 gap-6 sm:gap-8 lg:col-span-2">
              <div>
                <h3 className="text-sm font-semibold text-gray-900 tracking-wider uppercase">Product</h3>
                <ul className="mt-4 space-y-3">
                  <li>
                    <Link href="/solutions" className="text-sm text-gray-500 hover:text-gray-900 transition-colors">
                      Solutions
                    </Link>
                  </li>
                  <li>
                    <Link href="/pricing" className="text-sm text-gray-500 hover:text-gray-900 transition-colors">
                      Pricing
                    </Link>
                  </li>
                  <li>
                    <Link href="/integrations/hawksoft" className="text-sm text-gray-500 hover:text-gray-900 transition-colors">
                      HawkSoft Integration
                    </Link>
                  </li>
                  <li>
                    <Link href="/features/automatic-logging" className="text-sm text-gray-500 hover:text-gray-900 transition-colors">
                      Automatic Logging
                    </Link>
                  </li>
                  <li>
                    <Link href="/security" className="text-sm text-gray-500 hover:text-gray-900 transition-colors">
                      Security
                    </Link>
                  </li>
                  <li>
                    <Link href="/status" className="text-sm text-gray-500 hover:text-gray-900 transition-colors">
                      System Status
                    </Link>
                  </li>
                </ul>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-900 tracking-wider uppercase">Resources</h3>
                <ul className="mt-4 space-y-3">
                  <li>
                    <Link href="/docs" className="text-sm text-gray-500 hover:text-gray-900 transition-colors">
                      Docs & Guides
                    </Link>
                  </li>
                  <li>
                    <Link href="/faq" className="text-sm text-gray-500 hover:text-gray-900 transition-colors">
                      FAQ
                    </Link>
                  </li>
                  <li>
                    <Link href="/compliance/eo-documentation" className="text-sm text-gray-500 hover:text-gray-900 transition-colors">
                      E&O Documentation
                    </Link>
                  </li>
                  <li>
                    <Link href="/compliance/insurance-sms" className="text-sm text-gray-500 hover:text-gray-900 transition-colors">
                      SMS Compliance
                    </Link>
                  </li>
                  <li>
                    <Link href="/blog" className="text-sm text-gray-500 hover:text-gray-900 transition-colors">
                      Blog
                    </Link>
                  </li>
                  <li>
                    <Link href="/contact" className="text-sm text-gray-500 hover:text-gray-900 transition-colors">
                      Support
                    </Link>
                  </li>
                </ul>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-900 tracking-wider uppercase">Company</h3>
                <ul className="mt-4 space-y-3">
                  <li>
                    <Link href="/about" className="text-sm text-gray-500 hover:text-gray-900 transition-colors">
                      About
                    </Link>
                  </li>
                  <li>
                    <Link href="/contact" className="text-sm text-gray-500 hover:text-gray-900 transition-colors">
                      Contact
                    </Link>
                  </li>
                  <li>
                    <Link href="/request-demo" className="text-sm text-gray-500 hover:text-gray-900 transition-colors">
                      Book a Demo
                    </Link>
                  </li>
                </ul>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-900 tracking-wider uppercase">Legal</h3>
                <ul className="mt-4 space-y-3">
                  <li>
                    <Link href="/privacy" className="text-sm text-gray-500 hover:text-gray-900 transition-colors">
                      Privacy Policy
                    </Link>
                  </li>
                  <li>
                    <Link href="/terms" className="text-sm text-gray-500 hover:text-gray-900 transition-colors">
                      Terms of Service
                    </Link>
                  </li>
                </ul>
              </div>
            </div>
          </div>
          <div className="mt-12 border-t border-gray-200 pt-8">
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
              <p className="text-sm text-gray-400">
                {COMPANY.copyrightNotice}
              </p>
              <div className="flex items-center gap-6 text-sm text-gray-400">
                <span className="flex items-center gap-1.5">
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  Built in Salt Lake City
                </span>
                <Link href="/login" className="text-gray-500 hover:text-gray-900 transition-colors font-medium">
                  Login
                </Link>
                <span className="text-gray-300">·</span>
                <Link href="/platform/login" className="text-gray-400 hover:text-gray-600 transition-colors text-xs">
                  Admin Login
                </Link>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
    </>
  );
} 